import React, { useState, useEffect } from 'react';

/**
 * SplashScreen - Tela de carregamento animada com logo G2
 * 
 * Features:
 * - Logo com animação de pulse e scale
 * - Barra de progresso animada
 * - Mensagens de status rotativas
 * - Transição suave ao finalizar
 */
const SplashScreen = ({ onComplete, minDuration = 2000 }) => {
  const [progress, setProgress] = useState(0);
  const [statusIndex, setStatusIndex] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);

  const statusMessages = [
    'Conectando...',
    'Carregando seu perfil...',
    'Preparando ambiente...',
    'Quase pronto...'
  ];

  useEffect(() => {
    // Animação de progresso
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        // Progresso mais rápido no início, desacelera no final
        const increment = prev < 70 ? 8 : prev < 90 ? 4 : 2;
        return Math.min(prev + increment, 100);
      });
    }, 100);

    // Rotação de mensagens de status
    const statusInterval = setInterval(() => {
      setStatusIndex(prev => (prev + 1) % statusMessages.length);
    }, 800);

    // Garantir duração mínima e iniciar fade out
    const minTimer = setTimeout(() => {
      setProgress(100);
      setTimeout(() => {
        setFadeOut(true);
        setTimeout(() => {
          onComplete?.();
        }, 500); // Duração do fade out
      }, 300);
    }, minDuration);

    return () => {
      clearInterval(progressInterval);
      clearInterval(statusInterval);
      clearTimeout(minTimer);
    };
  }, [minDuration, onComplete, statusMessages.length]);

  return (
    <div 
      className={`fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-white to-cyan-50 transition-opacity duration-500 ${
        fadeOut ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Background decorativo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-cyan-100/30 to-transparent rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-blue-100/30 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Container principal */}
      <div className="relative flex flex-col items-center">
        {/* Logo com animações */}
        <div className="relative mb-8">
          {/* Círculos de animação de fundo */}
          <div className="absolute inset-0 -m-8">
            <div className="absolute inset-0 rounded-full border-2 border-cyan-200/50 animate-ping" style={{ animationDuration: '2s' }} />
            <div className="absolute inset-0 rounded-full border border-blue-200/30 animate-ping" style={{ animationDuration: '2.5s', animationDelay: '0.5s' }} />
          </div>

          {/* Logo principal */}
          <div className="relative">
            <div className="w-32 h-32 bg-white rounded-2xl shadow-lg flex items-center justify-center p-2 animate-logo-pulse">
              <img 
                src="/g2-splash-logo.png" 
                alt="G2eGente" 
                className="w-full h-full object-contain"
              />
            </div>
            
            {/* Brilho atrás da logo */}
            <div className="absolute inset-0 -z-10 bg-gradient-to-tr from-cyan-400/20 to-blue-400/20 rounded-2xl blur-xl scale-125 animate-pulse" />
          </div>
        </div>

        {/* Nome do app */}
        <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent mb-2 animate-fade-in">
          G2eGente
        </h1>

        {/* Status message */}
        <p className="text-slate-500 text-sm mb-6 h-5 transition-all duration-300 animate-fade-in">
          {statusMessages[statusIndex]}
        </p>

        {/* Barra de progresso */}
        <div className="w-48 h-1.5 bg-slate-200/70 rounded-full overflow-hidden backdrop-blur-sm">
          <div 
            className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all duration-200 ease-out relative"
            style={{ width: `${progress}%` }}
          >
            {/* Brilho na barra */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
          </div>
        </div>

        {/* Porcentagem */}
        <span className="text-xs text-slate-400 mt-2 font-medium tabular-nums">
          {Math.round(progress)}%
        </span>
      </div>

      {/* Rodapé */}
      <div className="absolute bottom-8 text-center">
        <p className="text-xs text-slate-400">
          Recrutamento Ético e Inclusivo
        </p>
      </div>

      {/* Estilos de animação inline */}
      <style>{`
        @keyframes logo-pulse {
          0%, 100% {
            transform: scale(1);
            filter: drop-shadow(0 4px 12px rgba(6, 182, 212, 0.3));
          }
          50% {
            transform: scale(1.05);
            filter: drop-shadow(0 8px 24px rgba(6, 182, 212, 0.4));
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        .animate-logo-pulse {
          animation: logo-pulse 2s ease-in-out infinite;
        }

        .animate-fade-in {
          animation: fade-in 0.6s ease-out forwards;
        }

        .animate-shimmer {
          animation: shimmer 1.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default SplashScreen;
